import { Injectable } from '@angular/core';
import { IConfiguredMenuItem } from './menu.models';

@Injectable({ providedIn: 'root' })
export class MenuFacade {
    buildReportsMenuItems(args: {
        isEicAssetMap: boolean;
        showSectorWorkbooks: boolean;
        showNewsBriefs: boolean;
        showOffshoreAssets: boolean;
        showPDFReportManager: boolean;
        suspendPdfReportManager: boolean;
        onInsightReports: () => void;
        onOpexReports: () => void;
        onCountryReports: () => void;
        onOffshoreAssets: () => void;
        onSectorWorkbooks: () => void;
        onNewsBriefs: () => void;
        onManagePdfReports: () => void;
    }): IConfiguredMenuItem[] {
        return [
            { id: 'mnuReports-InsightReports', label: 'Insight Reports (Capex)', onClick: args.onInsightReports },
            { id: 'mnuReports-OpexReports', label: 'Insight Reports (OPEX)', onClick: args.onOpexReports },
            { id: 'mnuReports-CountryReports', label: 'Country Reports', onClick: args.onCountryReports },
            { id: 'mnuReports-OffshoreAssets', label: 'Offshore Assets', visible: args.showOffshoreAssets, onClick: args.onOffshoreAssets },
            { id: 'mnuReports-SectorWorkbooks', label: 'Sector Workbooks', visible: args.showSectorWorkbooks, onClick: args.onSectorWorkbooks },
            { id: 'mnuReports-NewsBriefs', label: 'Newsbriefs', visible: args.showNewsBriefs, onClick: args.onNewsBriefs },
            {
                id: 'mnuReports-ManagePdfReports',
                label: args.suspendPdfReportManager ? 'Manage PDF Reports Suspended' : 'Manage PDF Reports',
                visible: args.showPDFReportManager,
                disabled: args.suspendPdfReportManager,
                onClick: args.onManagePdfReports
            }
        ];
    }

    buildToolsMenuItems(args: {
        selfServiceUserManagerVisible: boolean;
        apiKeyManagerVisible: boolean;
        enableApiKeyManager: boolean;
        apiSandboxVisible: boolean;
        enableApiSandbox: boolean;
        assetMarkerEditorVisible: boolean;
        enableAssetMarkerEditor: boolean;
        dataCentreEditorVisible: boolean;
        enableDataCentreEditor: boolean;
        productAccessManagerVisible: boolean;
        productAccessManagerUrl: string;
        onUserManager: () => void;
        onApiKeyManager: () => void;
        onApiSandbox: () => void;
        onAssetMarkerEditor: () => void;
        onDataCentreEditor: () => void;
    }): IConfiguredMenuItem[] {
        return [
            {
                id: 'mnuTools-SelfServiceUserManager',
                label: 'User Account Manager',
                visible: args.selfServiceUserManagerVisible,
                onClick: args.onUserManager
            },
            {
                id: 'mnuTools_ApiKeyManager',
                label: 'API Key Manager',
                visible: args.apiKeyManagerVisible && args.enableApiKeyManager,
                onClick: args.onApiKeyManager
            },
            {
                id: 'mnuTools_ApiSandbox',
                label: 'API Sandbox',
                visible: args.apiSandboxVisible && args.enableApiSandbox,
                onClick: args.onApiSandbox
            },
            {
                id: 'mnuTools_AssetMarkerEditor',
                label: 'Asset Marker Editor',
                visible: args.assetMarkerEditorVisible && args.enableAssetMarkerEditor,
                onClick: args.onAssetMarkerEditor
            },
            {
                id: 'mnuTools_DataCentreEditor',
                label: 'Data Centre Editor',
                visible: args.dataCentreEditorVisible && args.enableDataCentreEditor,
                onClick: args.onDataCentreEditor
            },
            {
                id: 'mnuTools-ProductAccessManager',
                label: 'Product Access Manager',
                visible: args.productAccessManagerVisible,
                href: args.productAccessManagerUrl,
                target: 'pam',
                showLaunchIcon: true
            }
        ];
    }

    buildSearchMenuItems(args: {
        isEicAssetMap: boolean;
        isNarrowScreen: boolean;
        isRepeatLastSearchEnabled: boolean;
        isAssetSearchEnabled: boolean;
        isEapUser: boolean;
        showSearchSeparator: boolean;
        onSavedSearches: () => void;
        onProjects: () => void;
        onProjectsMobile: () => void;
        onContracts: () => void;
        onContractsMobile: () => void;
        onRepeatLastSearch: () => void;
        onAssets: () => void;
        //onEgo: () => void;
    }): IConfiguredMenuItem[] {
        const showDesktopSearchItems = !args.isNarrowScreen;
        const showMobileSearchItems = args.isNarrowScreen;

        return [
            {
                id: 'mnuSearch-SavedSearches',
                label: 'Saved Searches',
                visible: !args.isEicAssetMap && showMobileSearchItems,
                onClick: args.onSavedSearches
            },
            {
                id: 'mnuSearch-CapexProjects',
                label: 'Projects',
                visible: !args.isEicAssetMap && showDesktopSearchItems,
                onClick: args.onProjects
            },
            {
                id: 'mnuSearch-CapexProjects-Mobile',
                label: 'Projects',
                visible: false,
                onClick: args.onProjectsMobile
            },
            {
                id: 'mnuSearch-Contracts',
                label: 'Contract Awards',
                visible: !args.isEicAssetMap && showDesktopSearchItems,
                onClick: args.onContracts
            },
            {
                id: 'mnuSearch-Contracts-Mobile',
                label: 'Contract Awards',
                visible: false,
                onClick: args.onContractsMobile
            },
            {
                id: 'mnuSearch-RepeatLastSearch',
                label: 'Repeat Last Search',
                visible: !args.isEicAssetMap,
                hidden: !args.isRepeatLastSearchEnabled,
                onClick: args.onRepeatLastSearch
            },
            {
                id: 'mnuSearch-separator',
                label: '',
                isSeparator: true,
                visible: args.showSearchSeparator && showDesktopSearchItems
            },
            {
                id: 'mnuSearch-Assets',
                label: 'Assets',
                visible: args.isAssetSearchEnabled && showDesktopSearchItems,
                onClick: args.onAssets
            },
            // {
            //     id: 'mnuSearch-EGO',
            //     label: 'E:GO',
            //     visible: args.isEapUser && showDesktopSearchItems,
            //     onClick: args.onEgo
            // }
        ];
    }

    buildEgoMenuItems(args: {
        isEicAssetMap: boolean;
        isEapUser: boolean;
        isEgoLive: boolean;
        onEgo: () => void;
    }): IConfiguredMenuItem[] {
        return [
            {
                id: 'mnuEgo-EGO',
                label: 'AI-assisted Project Searching',
                visible: !args.isEicAssetMap && (args.isEapUser || args.isEgoLive),
                onClick: args.onEgo
            }
        ];
    }

    buildNotificationMenuItems(args: { onNotifications: () => void }): IConfiguredMenuItem[] {
        return [{ id: 'mnuNotifications-NotificationCentre', label: 'Notification Centre', onClick: args.onNotifications }];
    }

    buildAnalysisMenuItems(args: {
        enableGeographicalSummary: boolean;
        geographicalProjectPresenterVisible: boolean;
        geographicalProjectPresenterUrl: string;
        onGeographicalSummary: () => void;
    }): IConfiguredMenuItem[] {
        return [
            {
                id: 'mnuAnalysis-GeographicalSummary',
                label: args.enableGeographicalSummary ? 'Geographical Summary' : 'Geographical Summary Suspended',
                disabled: !args.enableGeographicalSummary,
                onClick: args.onGeographicalSummary
            },
            {
                id: 'mnuTools-GeographicalProjectPresenter',
                label: 'Geographical Project Presenter',
                visible: args.geographicalProjectPresenterVisible,
                href: args.geographicalProjectPresenterUrl,
                target: 'gpp',
                showLaunchIcon: true
            }
        ];
    }

    buildProductsMenuItems(args: {
        eicAssetMapVisible: boolean;
        eicAssetMapUrl: string;
        eicDataStreamVisible: boolean;
        eicDataStreamUrl: string;
        eicSupplyMapUrl: string;
    }): IConfiguredMenuItem[] {
        return [
            {
                id: 'mnuProducts-EICAssetMap',
                label: 'EICAssetMap',
                visible: args.eicAssetMapVisible,
                href: args.eicAssetMapUrl,
                target: 'eicassetmap',
                showLaunchIcon: true
            },
            {
                id: 'mnuProducts-EICDataStream',
                label: 'EICDataStream',
                visible: args.eicDataStreamVisible,
                href: args.eicDataStreamUrl,
                target: 'eicdatastream',
                showLaunchIcon: true
            },
            {
                id: 'mnuProducts-EICSupplyMap',
                label: 'EICSupplyMap',
                href: args.eicSupplyMapUrl,
                target: 'eicsupplymap',
                showLaunchIcon: true
            }
        ];
    }

    buildHelpMenuItems(args: {
        isEicAssetMap: boolean;
        userGuideUrl: string;
        onSupportedPlatforms: () => void;
        onAssetMapUserGuide: () => void;
    }): IConfiguredMenuItem[] {
        return [
            { id: 'mnuHelp-SupportedPlatforms', label: 'Supported Platforms', onClick: args.onSupportedPlatforms },
            {
                id: 'mnuHelp-UserGuide',
                label: 'User Guide',
                visible: args.isEicAssetMap,
                onClick: args.onAssetMapUserGuide
            },
            {
                id: 'mnuHelp-UserGuide',
                label: 'User Guide',
                visible: !args.isEicAssetMap,
                href: args.userGuideUrl + '/intro',
                target: 'userguide',
                showLaunchIcon: true
            },
            {
                id: 'mnuHelp-Thresholds',
                label: 'Thresholds',
                href: args.userGuideUrl + '/guide/thresholds',
                target: 'userguide',
                showLaunchIcon: true
            },
            {
                id: 'mnuHelp-Glossary',
                label: 'Glossary',
                href: args.userGuideUrl + '/guide/glossary',
                target: 'userguide',
                showLaunchIcon: true
            }
        ];
    }

    buildYourAccountMenuItems(args: {
        isAuthenticated: boolean;
        changePasswordUrl: string;
        onUserInfo: () => void;
        onLogout: () => void;
        onLogin: () => void;
    }): IConfiguredMenuItem[] {
        return [
            { id: 'mnuYourAccount-AccountDetails', label: 'Account Details', onClick: args.onUserInfo },
            {
                id: 'mnuYourAccount-ChangeYourPassword',
                label: 'Change Your Password',
                href: args.changePasswordUrl,
                showLaunchIcon: true
            },
            { id: 'mnuYourAccount-LogOut', label: 'Log Out', visible: args.isAuthenticated, onClick: args.onLogout },
            { id: 'mnuYourAccount-LogIn', label: 'Log In', visible: !args.isAuthenticated, onClick: args.onLogin }
        ];
    }
}
